/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.25.1
 */

import { pageApi } from '@/main/api/page.api';
import {
    API_NEWS_ARCHIVE_LIST,
    API_NEWS_ARCHIVE,
    API_NEWS_MANGE,
    API_NEWS_PUBLISH_EXTERNAL,
    API_NEWS_PUBLISH_INTERNAL,
    API_NEWS_ARCHIVE_LIST_VIEWED,
    VIEWED,
} from '@/main/modules/app/Api';
import { newsTags, userNotificationsTags } from '@/main/modules/app/apiTags';
import { EHttpMethod } from '@/main/modules/app/appConstants';
import { invalidateOnSuccess } from '@/main/utils/invalidateOnSuccess';

import {
    INewsListItem,
    INewsItem,
    INewsItemRequest,
    INewsMangeDeleteItemRequest,
    IExternalNewsItem,
    IInternalNewsItem,
    ISendNewsItemViewedRequest,
} from './types';

export const userNotificationsApi = pageApi.injectEndpoints({
    endpoints: (builder) => ({
        // news archive
        getNewsList: builder.query<INewsListItem[], void>({
            query: () => ({
                url: API_NEWS_ARCHIVE_LIST,
            }),
            providesTags: [newsTags.NEWS_LIST],
        }),
        getNewsItem: builder.query<INewsItem, INewsItemRequest>({
            query: ({ id }) => ({
                url: `${API_NEWS_ARCHIVE}/${id}`,
            }),
        }),
        deleteManageNewsItem: builder.mutation<void, INewsMangeDeleteItemRequest>({
            query: ({ id }) => ({
                url: `${API_NEWS_MANGE}/${id}`,
                method: EHttpMethod.DELETE,
            }),
        }),
        publishExternalNewsItem: builder.mutation<void, IExternalNewsItem>({
            query: (args) => ({
                url: API_NEWS_PUBLISH_EXTERNAL,
                body: args,
                method: EHttpMethod.POST,
            }),
        }),
        publishInternalNewsItem: builder.mutation<void, IInternalNewsItem>({
            query: (args) => {
                const { file, ...restArgs } = args;
                const formData = new FormData();
                formData.append('news', new Blob([JSON.stringify(restArgs)], { type: 'application/json' }));
                if (file) {
                    formData.append('recipients', file);
                }

                return {
                    url: API_NEWS_PUBLISH_INTERNAL,
                    body: formData,
                    method: EHttpMethod.POST,
                    formData: true,
                };
            },
        }),
        sendNewsListViewed: builder.mutation<void, void>({
            query: () => ({
                url: API_NEWS_ARCHIVE_LIST_VIEWED,
                method: EHttpMethod.PUT,
            }),
            invalidatesTags: (_result, error) =>
                invalidateOnSuccess(error, [userNotificationsTags.USER_NOTIFICATIONS_LIST]),
        }),
        sendNewsItemViewed: builder.mutation<void, ISendNewsItemViewedRequest>({
            query: ({ id }) => ({
                url: `${API_NEWS_ARCHIVE}/${id}/${VIEWED}`,
                method: EHttpMethod.PUT,
            }),
            invalidatesTags: (_result, error) =>
                invalidateOnSuccess(error, [userNotificationsTags.USER_NOTIFICATIONS_LIST]),
        }),
    }),
});

export const {
    useGetNewsListQuery,
    useGetNewsItemQuery,
    useDeleteManageNewsItemMutation,
    usePublishExternalNewsItemMutation,
    usePublishInternalNewsItemMutation,
    useSendNewsListViewedMutation,
    useSendNewsItemViewedMutation,
} = userNotificationsApi;
